import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/FormField";
import { Input } from "@/components/ui/Input";
import { fetchJson, ApiError } from "@/lib/api";

import { useAuth } from "./AuthContext";

// Phase 2a customer-accounts FR-4. The standalone sign-in screen used
// when a customer hits /sign-in directly (the inline-checkout flow has
// its own embedded version in 2c). Layout mirrors the FR-4 sketch:
// email + password + Remember me + Sign in, with magic-link CTA below.
export function SignInPage(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") ?? "/account";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    try {
      await auth.signIn(email.trim().toLowerCase(), password);
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // FR-4: same message regardless of which field was wrong
        setError("Email or password is incorrect.");
      } else if (err instanceof ApiError && err.status === 429) {
        setError("Too many sign-in attempts. Wait a few minutes and try again.");
      } else {
        setError("Something went wrong signing you in. Please try again.");
      }
    }
  };

  const onMagicLink = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMagicError(null);
    setMagicSent(false);
    try {
      await fetchJson("/api/v1/auth/magic_link", {
        method: "POST",
        body: { email: magicEmail.trim().toLowerCase() }
      });
      setMagicSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setMagicError("Too many requests. Try again in a few minutes.");
      } else {
        setMagicError("Something went wrong. Try again.");
      }
    }
  };

  return (
    <section className="mx-auto max-w-md space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-brand-black">Sign in to JDR Rentals</h1>
        <p className="mt-1 text-sm text-zinc-600">
          New here?{" "}
          <Link to="/sign-up" className="text-brand-red underline">
            Create an account
          </Link>
          .
        </p>
      </header>

      <form onSubmit={onSubmit} noValidate className="space-y-4 rounded-md border border-zinc-200 bg-brand-white p-5">
        {error ? (
          <Alert variant="destructive" role="alert">
            {error}
          </Alert>
        ) : null}

        <FormField id="email" label="Email" required>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-required="true"
            required
          />
        </FormField>

        <FormField id="password" label="Password" required>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-required="true"
            required
          />
        </FormField>

        <div className="flex items-center justify-between">
          <Link to="/password/reset" className="text-sm text-brand-red underline">
            Forgot password?
          </Link>
        </div>

        <Button type="submit" disabled={auth.loading} className="w-full">
          {auth.loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <details className="rounded-md border border-zinc-200 bg-brand-white p-5">
        <summary className="cursor-pointer text-sm font-medium text-brand-black">
          Or sign in with a magic link
        </summary>
        <p className="mt-2 text-xs text-zinc-500">
          We&rsquo;ll email you a one-time link that signs you in without a password. Link expires in 15 minutes.
        </p>
        <form onSubmit={onMagicLink} noValidate className="mt-3 space-y-2">
          <FormField id="magic-email" label="Email">
            <Input
              id="magic-email"
              type="email"
              autoComplete="email"
              value={magicEmail}
              onChange={(e) => setMagicEmail(e.target.value)}
            />
          </FormField>
          {magicSent ? (
            <Alert variant="success" role="status">
              If that email is on file, we just sent you a sign-in link.
            </Alert>
          ) : null}
          {magicError ? (
            <Alert variant="destructive" role="alert">
              {magicError}
            </Alert>
          ) : null}
          <Button type="submit" variant="outline" className="w-full">
            Email me a sign-in link
          </Button>
        </form>
      </details>
    </section>
  );
}
